import { useMemo, useState, useCallback } from "react";
import {
  ActivityIndicator,
  View,
  StyleSheet,
  RefreshControl,
  TouchableOpacity,
  SectionList,
  Pressable,
} from "react-native";
import { router, useFocusEffect, useSegments } from "expo-router";
import { ThemedText } from "@/components/ThemedText";
import { supabase } from "@/lib/supabase-client";
import useAuth from "@/hooks/useAuth";
import { ShiftType } from "@/types/db/shifts";
import { useTheme } from "@/hooks/useTheme";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { ModernCard, StatusBadge, TimeChip } from "@/components/crew";
import Head from "expo-router/head";

interface GroupedShift {
  title: string;
  data: ShiftType[];
}

export default function SupervisingScreen() {
  const [shifts, setShifts] = useState<ShiftType[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState(new Set(["Today", "Tomorrow", "This Week", "Later"]));
  const { user, loading: authLoading, displayNames } = useAuth();
  const { colors } = useTheme();
  const segs = useSegments();
  const me = user?.id ?? "";
  const title = "Supervising";

  const fetchShifts = useCallback(
    async (showLoader = true) => {
      if (authLoading || !user?.id) {
        if (showLoader) setLoading(authLoading);
        return;
      }

      if (showLoader) setLoading(true);
      try {
        const { data: rawData, error: fnError } = await supabase.functions.invoke("get-shifts", {
          body: { role: "supervisor" },
        });
        if (fnError) throw new Error(fnError.message || "Failed to fetch shifts");
        const rows: ShiftType[] = Array.isArray(rawData) ? rawData : [];
        // Sort by start time
        const sortedShifts = (rows ?? []).sort((a, b) => {
          const aTime = a.start_time ? new Date(a.start_time).getTime() : 0;
          const bTime = b.start_time ? new Date(b.start_time).getTime() : 0;
          return aTime - bTime;
        });
        setShifts(sortedShifts);
        setErr(null);
      } catch (e: any) {
        setErr(e.message ?? `Failed to load ${displayNames?.shifts?.plural?.toLowerCase() || "shifts"}`);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [me, authLoading, user?.id, displayNames?.shifts?.plural],
  );

  useFocusEffect(
    useCallback(() => {
      if (!authLoading && user?.id) {
        fetchShifts();
      }
    }, [fetchShifts, authLoading, user?.id]),
  );

  const onRefresh = useCallback(() => {
    setRefreshing(true);
    fetchShifts(false);
  }, [fetchShifts]);

  // Group shifts by time period
  const groupedShifts = useMemo((): GroupedShift[] => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today.getTime() + 24 * 60 * 60 * 1000);
    const weekEnd = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);

    const groups: GroupedShift[] = [];
    const pastShifts: ShiftType[] = [];
    const todayShifts: ShiftType[] = [];
    const tomorrowShifts: ShiftType[] = [];
    const thisWeekShifts: ShiftType[] = [];
    const laterShifts: ShiftType[] = [];

    shifts.forEach((shift) => {
      if (!shift.start_time) {
        laterShifts.push(shift);
        return;
      }
      const startDate = new Date(shift.start_time);
      if (startDate < today) {
        pastShifts.push(shift);
      } else if (startDate < tomorrow) {
        todayShifts.push(shift);
      } else if (startDate < new Date(tomorrow.getTime() + 24 * 60 * 60 * 1000)) {
        tomorrowShifts.push(shift);
      } else if (startDate < weekEnd) {
        thisWeekShifts.push(shift);
      } else {
        laterShifts.push(shift);
      }
    });

    if (pastShifts.length > 0) groups.push({ title: "Completed", data: pastShifts });
    if (todayShifts.length > 0) groups.push({ title: "Today", data: todayShifts });
    if (tomorrowShifts.length > 0) groups.push({ title: "Tomorrow", data: tomorrowShifts });
    if (thisWeekShifts.length > 0) groups.push({ title: "This Week", data: thisWeekShifts });
    if (laterShifts.length > 0) groups.push({ title: "Later", data: laterShifts });

    return groups;
  }, [shifts]);

  const toggleSection = (title: string) => {
    setExpandedSections((expandedSections) => {
      const next = new Set(expandedSections);
      if (next.has(title)) {
        next.delete(title);
      } else {
        next.add(title);
      }
      return next;
    });
  };

  const getShiftStatus = (shift: ShiftType) => {
    if (!shift.start_time || !shift.end_time) return "upcoming";
    const now = new Date();
    const start = new Date(shift.start_time);
    const end = new Date(shift.end_time);

    if (now < start) return "upcoming";
    if (now > end) return "completed";
    return "active";
  };

  // Get venue type (shift-level or project fallback), null if undefined/Unspecified
  const getVenueType = (shift: ShiftType): string | null => {
    const shiftVenue = (shift.meta_data as any)?.venue_type;
    if (shiftVenue && shiftVenue !== "Unspecified") return shiftVenue;
    const projectVenue = (shift.project as any)?.meta_data?.venue_type;
    if (projectVenue && projectVenue !== "Unspecified") return projectVenue;
    return null;
  };

  // Get total assigned vs filled positions count
  const getPositionStats = (shift: ShiftType) => {
    const positions = shift.shift_positions || [];
    let totalHeadcount = 0;
    let filledCount = 0;

    positions.forEach((pos) => {
      const headcount = (pos as any).headcount || 1;
      totalHeadcount += headcount;
      const acceptedUsers = (pos.shift_users || []).filter((su) => su.status === "ACCEPTED").length;
      filledCount += Math.min(acceptedUsers, headcount);
    });

    return { totalHeadcount, filledCount };
  };

  const ListFooter = () => (
    <View style={styles.listFooter}>
      <MaterialCommunityIcons name="check-circle-outline" size={24} color={colors.placeholder} />
      <ThemedText style={[styles.footerText, { color: colors.textSecondary }]}>You&apos;re all caught up</ThemedText>
    </View>
  );

  const renderShiftCard = ({ item: shift }: { item: ShiftType }) => {
    const status = getShiftStatus(shift);
    const { totalHeadcount, filledCount } = getPositionStats(shift);

    const startTime = shift.start_time ? new Date(shift.start_time) : null;
    const endTime = shift.end_time ? new Date(shift.end_time) : null;
    const duration =
      startTime && endTime ? Math.round(((endTime.getTime() - startTime.getTime()) / (1000 * 60 * 60)) * 10) / 10 : 0;

    const getStatusVariant = () => {
      switch (status) {
        case "active":
          return "active";
        case "completed":
          return "completed";
        default:
          return "ready";
      }
    };

    const getStatusText = () => {
      switch (status) {
        case "active":
          return "IN PROGRESS";
        case "completed":
          return "COMPLETED";
        default:
          return "UPCOMING";
      }
    };

    const getStatusIcon = () => {
      switch (status) {
        case "active":
          return "clock-fast";
        case "completed":
          return "check-circle";
        default:
          return "clock-outline";
      }
    };

    return (
      <View style={styles.timelineItem}>
        {/* Timeline connector */}
        <View style={[styles.timelineConnector, { backgroundColor: colors.border }]} />

        {/* Timeline dot */}
        <View
          style={[
            styles.timelineDot,
            { backgroundColor: status === "completed" ? colors.emerald?.border : colors.primary },
          ]}
        />

        {/* Card */}
        <TouchableOpacity
          onPress={() =>
            router.push({
              pathname:
                `${(segs as string[]).includes("crew") ? "crew/supervising" : "supervising"}/${shift.id}` as any,
            })
          }
          activeOpacity={0.7}
          style={styles.cardTouchable}
        >
          <ModernCard style={styles.modernShiftCard}>
            <View style={styles.modernCardHeader}>
              <View style={{ flex: 1 }}>
                <ThemedText style={styles.modernProjectName} numberOfLines={1}>
                  {shift.name || `Untitled ${displayNames?.shifts?.name || "Shift"}`}
                  {"  "}
                  <ThemedText style={[styles.shiftId, { color: colors.placeholder }]}>
                    {shift.id?.slice(0, 6).toUpperCase()}
                  </ThemedText>
                </ThemedText>
                {(shift.project?.client?.name || shift.project?.name) && (
                  <ThemedText style={[styles.modernPositionName, { color: colors.textSecondary }]} numberOfLines={1}>
                    {[shift.project?.client?.name, shift.project?.name].filter(Boolean).join(" · ")}
                  </ThemedText>
                )}
                {(() => {
                  const loc = shift.location || shift.project?.location;
                  if (!loc?.name) return null;
                  const addr = [loc.address?.city, loc.address?.state].filter(Boolean).join(", ");
                  return (
                    <ThemedText style={[styles.modernPositionName, { color: colors.textSecondary }]} numberOfLines={1}>
                      {addr ? `${loc.name} · ${addr}` : loc.name}
                    </ThemedText>
                  );
                })()}
              </View>

              <StatusBadge variant={getStatusVariant()} text={getStatusText()} icon={getStatusIcon()} size="small" />
            </View>

            <View style={styles.modernCardDetails}>
              {startTime && (
                <TimeChip
                  type="status"
                  text={startTime.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" })}
                  size="small"
                />
              )}

              {startTime && (
                <TimeChip
                  type="time"
                  text={startTime.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  size="small"
                />
              )}

              {duration > 0 && <TimeChip type="duration" text={`~${duration}h`} size="small" />}

              {/* Show staffing status */}
              <TimeChip type="status" text={`${filledCount}/${totalHeadcount} filled`} size="small" />

              {getVenueType(shift) && <TimeChip type="status" text={getVenueType(shift)!} size="small" />}
            </View>
          </ModernCard>
        </TouchableOpacity>
      </View>
    );
  };

  if (loading) {
    return (
      <View style={[styles.container, styles.center, { backgroundColor: colors.background }]}>
        <ActivityIndicator size="large" color={colors.primary} />
        <ThemedText style={{ marginTop: 12, opacity: 0.6 }}>
          Loading supervised {displayNames?.shifts?.plural?.toLowerCase() || "shifts"}...
        </ThemedText>
      </View>
    );
  }

  if (err) {
    return (
      <View style={[styles.container, styles.center, { backgroundColor: colors.background }]}>
        <MaterialCommunityIcons name="alert-circle-outline" size={48} color={colors.red.border} />
        <ThemedText style={{ marginTop: 12, fontSize: 16 }}>{err}</ThemedText>
      </View>
    );
  }

  if (shifts.length === 0) {
    return (
      <View style={[styles.container, styles.center, { backgroundColor: colors.background }]}>
        <MaterialCommunityIcons name="account-tie-outline" size={64} color={colors.placeholder} />
        <ThemedText style={styles.emptyTitle}>No Supervised {displayNames?.shifts?.plural || "Shifts"}</ThemedText>
        <ThemedText style={[styles.emptySubtitle, { color: colors.textSecondary }]}>
          {displayNames?.shifts?.plural || "Shifts"} you supervise will appear here
        </ThemedText>
      </View>
    );
  }

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="description" content="" />
      </Head>
      <View style={[styles.container, { backgroundColor: colors.background }]}>
        <SectionList
          sections={groupedShifts}
          extraData={expandedSections}
          keyExtractor={(item, index) => item.id ?? `shift-${index}`}
          renderItem={({ section: { title }, item }) => {
            if (!expandedSections.has(title)) return null;
            return renderShiftCard({ item });
          }}
          renderSectionHeader={({ section }) => (
            <View style={[styles.sectionHeader, { backgroundColor: colors.background }]}>
              <Pressable onPress={() => toggleSection(section.title)}>
                <ThemedText style={[styles.sectionTitle, { color: colors.text }]}>
                  <TimeChip
                    type="duration"
                    style={{ minWidth: 50, justifyContent: "center" }}
                    text={section.data.length.toString()}
                    size="small"
                  />{" "}
                  {section.title}
                  <MaterialCommunityIcons
                    name={expandedSections.has(section.title) ? "chevron-down" : "chevron-up"}
                    size={18}
                    color={colors.placeholder}
                  />
                </ThemedText>
              </Pressable>
            </View>
          )}
          contentContainerStyle={styles.listContent}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={colors.primary} />}
          showsVerticalScrollIndicator={false}
          stickySectionHeadersEnabled={false}
          ListFooterComponent={shifts.length > 0 ? ListFooter : null}
        />
      </View>
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  center: {
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
  },

  // List
  listContent: {
    paddingBottom: 32,
  },
  sectionHeader: {
    paddingHorizontal: 20,
    paddingTop: 20,
    paddingBottom: 8,
  },
  sectionTitle: {
    fontSize: 15,
    fontWeight: "600",
    textTransform: "uppercase",
    opacity: 0.7,
  },

  // Timeline & Cards
  timelineItem: {
    flexDirection: "row",
    alignItems: "flex-start",
    paddingHorizontal: 16,
    marginBottom: 16,
    position: "relative",
  },
  timelineConnector: {
    position: "absolute",
    left: 27,
    top: 20,
    bottom: -16,
    width: 2,
    opacity: 0.3,
  },
  timelineDot: {
    width: 12,
    height: 12,
    borderRadius: 6,
    marginTop: 8,
    marginRight: 16,
    zIndex: 1,
  },
  cardTouchable: {
    flex: 1,
  },
  modernShiftCard: {
    margin: 0,
  },
  modernCardHeader: {
    flexDirection: "row",
    alignItems: "flex-start",
    marginBottom: 12,
    gap: 8,
  },
  modernProjectName: {
    fontSize: 16,
    fontWeight: "600",
    marginBottom: 2,
  },
  shiftId: {
    fontSize: 12,
    fontWeight: "400",
  },
  modernPositionName: {
    fontSize: 13,
  },
  modernCardDetails: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },

  // Empty state
  emptyTitle: {
    fontSize: 18,
    fontWeight: "600",
    marginTop: 16,
  },
  emptySubtitle: {
    fontSize: 14,
    marginTop: 4,
  },
  listFooter: {
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 32,
    gap: 8,
  },
  footerText: {
    fontSize: 14,
  },
});
